import { Tooltip } from './ui/Tooltip';
import type { FieldConfidence } from '../types/extraction';

/**
 * PROD1.7.8 N3 — Icône de fiabilité pour un champ extrait par le LLM.
 *
 * 🟢 high   — extraction directe, source claire et unique.
 * 🟡 medium — déduction nécessaire, ou plusieurs candidats défensibles.
 * 🔴 low    — ambiguïté forte, valeur incertaine.
 *
 * `reason` (optionnel, fourni par le LLM pour medium/low) est affichée en
 * tooltip au hover de l'icône.
 */
interface ConfidenceIconProps {
  level: FieldConfidence['confidence'];
  reason?: string | null;
  /** Compact = inline (default size), small = encore plus petit pour tableaux. */
  size?: 'inline' | 'small';
}

const LEVEL_META: Record<
  FieldConfidence['confidence'],
  { emoji: string; label: string; defaultReason: string }
> = {
  high: {
    emoji: '🟢',
    label: 'Confiance élevée',
    defaultReason: 'Extraction directe, source claire et unique.',
  },
  medium: {
    emoji: '🟡',
    label: 'Confiance moyenne',
    defaultReason: 'Déduction nécessaire ou plusieurs candidats défensibles.',
  },
  low: {
    emoji: '🔴',
    label: 'Confiance faible',
    defaultReason: 'Ambiguïté forte — à vérifier manuellement.',
  },
};

export function ConfidenceIcon({ level, reason, size = 'inline' }: ConfidenceIconProps) {
  const meta = LEVEL_META[level];
  if (!meta) return null;
  const txt = reason && reason.trim() ? `${meta.label} — ${reason}` : `${meta.label} — ${meta.defaultReason}`;
  const sizeCls = size === 'small' ? 'text-xs' : 'text-sm';
  return (
    <Tooltip content={txt}>
      <span className={`${sizeCls} cursor-help select-none`} aria-label={meta.label}>
        {meta.emoji}
      </span>
    </Tooltip>
  );
}
